import React, { useState } from 'react';
import { useToast } from '../context/ToastContext';
import { finalizarOnboarding } from '../services/onboardingService';
import { mensajeAmigable } from '../utils/errores';
import {
  PAISES, TIPOS_USUARIO, RUBROS, EMPLEADOS, ANTIGUEDAD, TAMANO_VENTAS,
  OBJETIVOS, NECESIDADES, COMO_CONOCIO,
} from '../utils/onboarding';
import { OPCIONES_VENDE } from '../utils/queVende';

// Onboarding después del registro. Corto, moderno y con mínima fricción:
// todo con botones/chips seleccionables, 3 pasos, cada pregunta es opcional
// y se puede "completar después". Aparece una sola vez.

const P = 'var(--color-primario,#6D28D9)';

// Chip seleccionable (uno o varios). `activo` lo pinta con el color de marca.
function Chip({ activo, children, onClick }) {
  return (
    <button type="button" onClick={onClick}
      style={{
        padding: '10px 14px', borderRadius: '999px', fontSize: '14px', fontWeight: 600,
        cursor: 'pointer', transition: 'all .15s', lineHeight: 1.2,
        border: activo ? `1.5px solid ${P}` : '1.5px solid #E5E7EB',
        background: activo ? 'color-mix(in srgb, var(--color-primario,#6D28D9) 12%, white)' : '#fff',
        color: activo ? P : '#374151',
      }}>
      {children}
    </button>
  );
}

// Bloque de una pregunta: título + grilla de chips.
function Pregunta({ titulo, hint, children }) {
  return (
    <div style={{ marginBottom: '22px' }}>
      <label style={{ display: 'block', fontSize: '15px', fontWeight: 700, color: '#111827', marginBottom: hint ? '2px' : '10px' }}>{titulo}</label>
      {hint && <p style={{ margin: '0 0 10px', fontSize: '12.5px', color: '#9CA3AF' }}>{hint}</p>}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>{children}</div>
    </div>
  );
}

const TOTAL_PASOS = 3;

export default function OnboardingModal({ nombreNegocio, onListo }) {
  const { mostrarToast } = useToast();
  const [paso, setPaso] = useState(1);
  const [guardando, setGuardando] = useState(false);
  const [r, setR] = useState({
    tipo_usuario: '', como_conocio: '', como_conocio_otro: '',
    pais: '', vende: '', rubro: '', rubro_otro: '', empleados: '', antiguedad: '', tamano_ventas: '',
    objetivo_principal: '', objetivo_otro: '', necesidades: [],
  });

  const set = (campo, valor) => setR((prev) => ({ ...prev, [campo]: valor }));
  // Para selección única: tocar de nuevo deselecciona (todo es opcional).
  const toggleUnico = (campo, clave) => set(campo, r[campo] === clave ? '' : clave);
  const toggleLista = (campo, clave) => set(campo, r[campo].includes(clave)
    ? r[campo].filter((x) => x !== clave)
    : [...r[campo], clave]);

  const cerrar = async (omitido) => {
    setGuardando(true);
    try {
      await finalizarOnboarding(r, { omitido });
      if (!omitido) mostrarToast('¡Gracias! Ya puedes usar Kontrol 💜', 'exito');
    } catch (e) {
      mostrarToast(mensajeAmigable(e), 'error');
    } finally {
      setGuardando(false);
      onListo(omitido ? null : (r.rubro || null), omitido ? null : (r.vende || null)); // entra a la app (marca local para no re-mostrar)
    }
  };

  const siguiente = () => setPaso((p) => Math.min(TOTAL_PASOS, p + 1));
  const anterior = () => setPaso((p) => Math.max(1, p - 1));

  return (
    <div style={{ minHeight: '100vh', background: '#FDF2F8', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '16px', fontFamily: 'Arial, sans-serif', overflowY: 'auto' }}>
      <div style={{ background: '#fff', borderRadius: '20px', padding: '26px 22px', width: '480px', maxWidth: '100%', margin: '18px 0', boxShadow: '0 8px 30px rgba(109,40,217,0.15)', boxSizing: 'border-box' }}>

        {/* Encabezado + progreso */}
        <div style={{ marginBottom: '18px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <span style={{ fontSize: '12.5px', fontWeight: 700, color: P }}>Paso {paso} de {TOTAL_PASOS}</span>
            <button type="button" onClick={() => cerrar(true)} disabled={guardando}
              style={{ background: 'none', border: 'none', color: '#9CA3AF', fontSize: '12.5px', cursor: 'pointer', textDecoration: 'underline' }}>
              Completar después
            </button>
          </div>
          <div style={{ display: 'flex', gap: '5px' }}>
            {[1, 2, 3].map((n) => (
              <div key={n} style={{ flex: 1, height: '5px', borderRadius: '999px', background: n <= paso ? P : '#EDE9FE' }} />
            ))}
          </div>
        </div>

        {/* PASO 1 — Sobre ti */}
        {paso === 1 && (
          <div>
            <h2 style={{ margin: '0 0 4px', fontSize: '20px', color: '#111827' }}>¡Te damos la bienvenida a Kontrol! 🎉</h2>
            <p style={{ margin: '0 0 20px', fontSize: '13.5px', color: '#6B7280' }}>
              Cuéntanos un poco para adaptar Kontrol a {nombreNegocio ? <strong>{nombreNegocio}</strong> : 'tu negocio'}. Son 3 pasitos rápidos.
            </p>
            <Pregunta titulo="¿Cómo te describes?">
              {TIPOS_USUARIO.map((o) => (
                <Chip key={o.clave} activo={r.tipo_usuario === o.clave} onClick={() => toggleUnico('tipo_usuario', o.clave)}>
                  {o.emoji} {o.label}
                </Chip>
              ))}
            </Pregunta>
            <Pregunta titulo="¿Cómo conociste Kontrol?">
              {COMO_CONOCIO.map((o) => (
                <Chip key={o.clave} activo={r.como_conocio === o.clave} onClick={() => toggleUnico('como_conocio', o.clave)}>
                  {o.emoji} {o.label}
                </Chip>
              ))}
            </Pregunta>
            {r.como_conocio === 'otro' && (
              <input value={r.como_conocio_otro} maxLength={80} placeholder="¿Cómo? (opcional)"
                onChange={(e) => set('como_conocio_otro', e.target.value)}
                style={inputEstilo} />
            )}
          </div>
        )}

        {/* PASO 2 — Tu negocio */}
        {paso === 2 && (
          <div>
            <h2 style={{ margin: '0 0 20px', fontSize: '20px', color: '#111827' }}>Sobre tu negocio 🏪</h2>
            <div style={{ marginBottom: '22px' }}>
              <label style={{ display: 'block', fontSize: '15px', fontWeight: 700, color: '#111827', marginBottom: '10px' }}>¿En qué país está tu negocio?</label>
              <select value={r.pais} onChange={(e) => set('pais', e.target.value)}
                style={{ width: '100%', padding: '11px', border: `1.5px solid ${r.pais ? P : '#E5E7EB'}`, borderRadius: '10px', fontSize: '14px', boxSizing: 'border-box', background: '#fff' }}>
                <option value="">Elige tu país…</option>
                {PAISES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <Pregunta titulo="¿Qué vendes?" hint="Así te mostramos solo lo que usas. Lo puedes cambiar cuando quieras en Configuración.">
              {OPCIONES_VENDE.map((o) => (
                <Chip key={o.clave} activo={r.vende === o.clave} onClick={() => toggleUnico('vende', o.clave)}>
                  {o.emoji} {o.label}
                </Chip>
              ))}
            </Pregunta>
            <Pregunta titulo="¿A qué se dedica?">
              {RUBROS.map((o) => (
                <Chip key={o.clave} activo={r.rubro === o.clave} onClick={() => toggleUnico('rubro', o.clave)}>
                  {o.emoji} {o.label}
                </Chip>
              ))}
            </Pregunta>
            {r.rubro === 'otro' && (
              <input value={r.rubro_otro} maxLength={60} placeholder="¿Cuál rubro? (opcional)"
                onChange={(e) => set('rubro_otro', e.target.value)}
                style={{ ...inputEstilo, marginTop: '-12px' }} />
            )}
            <Pregunta titulo="¿Cuántas personas trabajan contigo?">
              {EMPLEADOS.map((o) => (
                <Chip key={o.clave} activo={r.empleados === o.clave} onClick={() => toggleUnico('empleados', o.clave)}>{o.label}</Chip>
              ))}
            </Pregunta>
            <Pregunta titulo="¿Cuánto tiempo tiene tu negocio?">
              {ANTIGUEDAD.map((o) => (
                <Chip key={o.clave} activo={r.antiguedad === o.clave} onClick={() => toggleUnico('antiguedad', o.clave)}>{o.label}</Chip>
              ))}
            </Pregunta>
            <Pregunta titulo="¿Cuánto vendes al mes, más o menos?" hint="Un rango aproximado, en tu moneda. Nadie más lo ve.">
              {TAMANO_VENTAS.map((o) => (
                <Chip key={o.clave} activo={r.tamano_ventas === o.clave} onClick={() => toggleUnico('tamano_ventas', o.clave)}>{o.label}</Chip>
              ))}
            </Pregunta>
          </div>
        )}

        {/* PASO 3 — Tus metas */}
        {paso === 3 && (
          <div>
            <h2 style={{ margin: '0 0 20px', fontSize: '20px', color: '#111827' }}>¿Qué quieres lograr? 🎯</h2>
            <Pregunta titulo="Tu objetivo principal con Kontrol">
              {OBJETIVOS.map((o) => (
                <Chip key={o.clave} activo={r.objetivo_principal === o.clave} onClick={() => toggleUnico('objetivo_principal', o.clave)}>
                  {o.emoji} {o.label}
                </Chip>
              ))}
            </Pregunta>
            {r.objetivo_principal === 'otro' && (
              <input value={r.objetivo_otro} maxLength={80} placeholder="¿Cuál objetivo? (opcional)"
                onChange={(e) => set('objetivo_otro', e.target.value)}
                style={{ ...inputEstilo, marginTop: '-12px' }} />
            )}
            <Pregunta titulo="¿Qué te gustaría resolver?" hint="Puedes elegir varias.">
              {NECESIDADES.map((o) => (
                <Chip key={o.clave} activo={r.necesidades.includes(o.clave)} onClick={() => toggleLista('necesidades', o.clave)}>
                  {o.emoji} {o.label}
                </Chip>
              ))}
            </Pregunta>
          </div>
        )}

        {/* Navegación */}
        <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
          {paso > 1 && (
            <button type="button" onClick={anterior} disabled={guardando}
              style={{ padding: '13px 18px', borderRadius: '12px', border: '1.5px solid #E5E7EB', background: '#fff', color: '#374151', fontSize: '15px', fontWeight: 700, cursor: 'pointer' }}>
              ← Atrás
            </button>
          )}
          {paso < TOTAL_PASOS ? (
            <button type="button" onClick={siguiente} disabled={guardando}
              style={{ flex: 1, padding: '13px', borderRadius: '12px', border: 'none', background: P, color: '#fff', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer' }}>
              Continuar →
            </button>
          ) : (
            <button type="button" onClick={() => cerrar(false)} disabled={guardando}
              style={{ flex: 1, padding: '13px', borderRadius: '12px', border: 'none', background: guardando ? '#9CA3AF' : P, color: '#fff', fontSize: '15px', fontWeight: 'bold', cursor: guardando ? 'wait' : 'pointer' }}>
              {guardando ? '⏳ Guardando…' : 'Finalizar y entrar 🚀'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const inputEstilo = { width: '100%', padding: '11px', border: '1px solid #D1D5DB', borderRadius: '10px', fontSize: '14px', boxSizing: 'border-box', marginBottom: '22px' };
